import React, { useState } from "react";
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  Platform,
  Modal,
  ScrollView,
} from "react-native";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppImages } from "../../utility/AppImages";
import { CommonButton } from "../../common/CommonButton";

interface TwitchConnectedModalProps {
  isVisible: boolean;
  onClose: () => void;
  twitchUsername: string;
  onSkip: () => void;
  onGoToEventPage: () => void;
}

const TwitchConnectedModal: React.FC<TwitchConnectedModalProps> = ({
  isVisible,
  onClose,
  twitchUsername,
  onSkip,
  onGoToEventPage,
}) => {
  const [alreadyEnabled, setAlreadyEnabled] = useState(false);

  if (!isVisible) return null;

  const openTwitchSettings = () => {
    if (Platform.OS === "web") {
      window.open(
        `https://dashboard.twitch.tv/u/${twitchUsername}/settings/stream`,
        "_blank"
      );
    }
    // Close the modal after opening Twitch settings
    handleClose();
  };

  const handleClose = () => {
    if (Platform.OS === "web") {
      sessionStorage.setItem("twitchConnectedModalShown", "true");
    }
    onClose();
  };

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={isVisible}
      onRequestClose={handleClose}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0, 0, 0, 0.85)",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Outer Box */}
        <View
          style={{
            backgroundColor: AppColors.purple_453B50,
            borderRadius: 24,
            borderWidth: 1,
            borderColor: "#A6A3AA",
            padding: 20,
            maxWidth: 600,
            width: "90%",
            maxHeight: "85%",
          }}
        >
          <ScrollView showsVerticalScrollIndicator={false}>
            {/* Title */}
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_20,
                {
                  color: AppColors.white,
                  textAlign: "center",
                  marginBottom: 16,
                },
              ]}
            >
              Twitch Connected Successfully!
            </Text>

            {/* Inner Black Box */}
            <View
              style={{
                backgroundColor: "#1E1E1E",
                borderRadius: 24,
                padding: 20,
                marginBottom: 20,
              }}
            >
              {/* Logos */}
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: 24,
                }}
              >
                <Image
                  source={AppImages.icKOLogo}
                  style={{ width: 60, height: 60 }}
                  resizeMode="contain"
                />
                <Image
                  source={AppImages.icLinkPurple}
                  style={{ width: 30, height: 30, marginHorizontal: 12 }}
                  resizeMode="contain"
                />
                <Image
                  source={AppImages.icTwitchLogo}
                  style={{ width: 60, height: 60 }}
                  resizeMode="contain"
                />
              </View>

              {/* Success Message */}
              <Text
                style={[
                  AppFontStyle.SEMIBOLD_ANYBODY_18,
                  {
                    color: AppColors.appGreenColor_51AF07,
                    textAlign: "center",
                    marginBottom: 16,
                  },
                ]}
              >
                Your Twitch account is now linked with Kallout.
              </Text>

              {/* Instructions */}
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_16,
                  {
                    color: AppColors.white,
                    textAlign: "center",
                    marginBottom: 20,
                  },
                ]}
              >
                One last step: turn on{" "}
                <Text style={{ fontWeight: "600" }}>Store Past Broadcasts</Text>{" "}
                so your streams automatically save and show up in Kallout.
              </Text>

              {/* Steps */}
              <Text
                style={[
                  AppFontStyle.SEMIBOLD_ANYBODY_14,
                  { color: AppColors.white },
                ]}
              >
                1. Go to Twitch Settings
              </Text>
              <Text
                style={[
                  AppFontStyle.SEMIBOLD_ANYBODY_14,
                  { color: AppColors.white },
                ]}
              >
                2. Open Stream → VOD Settings
              </Text>
              <Text
                style={[
                  AppFontStyle.SEMIBOLD_ANYBODY_14,
                  { color: AppColors.white, marginBottom: 16 },
                ]}
              >
                3. Turn ON Store past broadcasts
              </Text>

              {/* Screenshot */}
              <Image
                source={AppImages.Store_Past_Stream}
                style={{
                  width: "100%",
                  height: 120,
                  borderRadius: 8,
                  marginBottom: 16,
                }}
                resizeMode="contain"
              />

              {/* Purple Instruction Block */}
              <View
                style={{
                  backgroundColor: AppColors.purple_453B50,
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 20,
                  alignItems: "center",
                }}
              >
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_14,
                    {
                      color: AppColors.white,
                      textAlign: "center",
                      marginBottom: 12,
                    },
                  ]}
                >
                  Click below to open Twitch settings. If you already enable the
                  store past broadcasts option you can go to event page.
                </Text>

                <CommonButton
                  title="Open Twitch settings"
                  onTap={openTwitchSettings}
                  buttonStyle={{
                    backgroundColor: "#7C67A3",
                    borderRadius: 8,
                    paddingVertical: 10,
                    width: 230,
                    borderWidth: 1,
                    borderColor: "#A6A3AA",
                  }}
                  fontStyle={[
                    AppFontStyle.SEMIBOLD_ANYBODY_14,
                    { color: AppColors.white, textAlign: "center" },
                  ]}
                />
              </View>

              {/* Checkbox */}
              <TouchableOpacity
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                }}
                onPress={() => setAlreadyEnabled(!alreadyEnabled)}
              >
                <View
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    borderWidth: 2,
                    borderColor: AppColors.gray_C4C4C4,
                    backgroundColor: alreadyEnabled
                      ? AppColors.appViolentColor_6D08C3
                      : "transparent",
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: 8,
                  }}
                >
                  {alreadyEnabled && (
                    <Image
                      source={AppImages.icCheckedGreen}
                      style={{ width: 12, height: 12 }}
                      resizeMode="contain"
                    />
                  )}
                </View>
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_14,
                    { color: AppColors.white },
                  ]}
                >
                  I already enable it
                </Text>
              </TouchableOpacity>
            </View>

            {/* Bottom Buttons */}
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                paddingHorizontal: 8,
                marginTop: 12,
              }}
            >
              <CommonButton
                title="Skip, I'll Do Later"
                onTap={() => {
                  onSkip();
                  handleClose();
                }}
                buttonStyle={{
                  backgroundColor: AppColors.white, // White/light background
                  borderRadius: 8,
                  paddingVertical: 10,
                  paddingHorizontal: 16,
                  borderWidth: 1,
                  borderColor: "#7C67A3",
                  minWidth: 140,
                }}
                fontStyle={[
                  AppFontStyle.SEMIBOLD_ANYBODY_14,
                  { color: AppColors.black_040404, textAlign: "center" }, // Purple text
                ]}
              />

              <CommonButton
                title="Go to Event Page"
                onTap={() => {
                  onGoToEventPage();
                  handleClose();
                }}
                buttonStyle={{
                  backgroundColor: AppColors.appViolentColor_6D08C3, // Purple background
                  borderRadius: 8,
                  paddingVertical: 10,
                  paddingHorizontal: 16,
                  borderWidth: 1,
                  borderColor: "#A6A3AA",
                  minWidth: 140,
                }}
                fontStyle={[
                  AppFontStyle.SEMIBOLD_ANYBODY_14,
                  { color: AppColors.white, textAlign: "center" },
                ]}
              />
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

export default TwitchConnectedModal;
